import type { FunnelQueryRequest, FunnelStepResult } from "@/api/insights";
import type {
  ListFunnelsResponse,
  PageViewFunnelQueryRequest,
} from "@/api/funnels";
import type { Funnel, FunnelStepConfig } from "@/funnels/types";

import {
  getAttributionDataset,
  type PreviewFunnelSpec,
} from "../attribution/dataset";
import { countStep, computePatternStepCounts } from "../graph/aggregates";
import { getPreviewGraph } from "../graph";
import { getPreviewProfile } from "../profiles";
import type { PreviewFunnel } from "../graph/types";
import { PREVIEW_ENV_ID } from "../runtime";

function toApiFunnel(f: PreviewFunnel): Funnel {
  const steps: FunnelStepConfig[] = f.steps.map((s) => ({
    type: "pageView",
    label: s.label,
    urlPatterns: s.urlPatterns,
  }));
  return {
    id: f.id,
    envID: PREVIEW_ENV_ID,
    name: f.name,
    config: { steps },
    createdAt: new Date(f.createdAt).toISOString(),
    updatedAt: new Date(f.updatedAt).toISOString(),
  };
}

// Adapts a profile-dataset funnel spec (fixed stepCounts, no synthetic
// session graph behind it) into the internal PreviewFunnel shape used by
// the query helpers below.
function specToPreviewFunnel(
  spec: PreviewFunnelSpec,
  now: number,
): PreviewFunnel {
  const dayMs = 24 * 60 * 60 * 1000;
  return {
    id: spec.id,
    name: spec.name,
    description: spec.description,
    steps: spec.steps.map((s) => ({
      eventName: "page_view",
      label: s.label,
      urlPatterns: [...s.urlPatterns],
    })),
    stepCounts: [...spec.stepCounts],
    createdAt: now - spec.createdAtDaysAgo * dayMs,
    updatedAt: now,
  };
}

function activeFunnels(): PreviewFunnel[] {
  const profile = getPreviewProfile();
  if (profile === "pharma") {
    // Pharma has no synthetic session graph — the counts are baked into
    // the dataset. Use the current timestamp so `updatedAt` reads as
    // recent in the funnels list.
    const now = Date.now();
    return getAttributionDataset(profile).funnels.map((f) =>
      specToPreviewFunnel(f, now),
    );
  }
  return [...getPreviewGraph().funnels];
}

export function handleListFunnels(
  _params: Record<string, never>,
): ListFunnelsResponse {
  return { funnels: activeFunnels().map(toApiFunnel) };
}

export function handleAnalyticsFunnelQuery(
  params: FunnelQueryRequest,
): FunnelStepResult[] {
  const funnels = activeFunnels();
  const stepNames = params.steps.map((s) => s.eventName);

  const match = funnels.find(
    (f) =>
      f.steps.length === stepNames.length &&
      f.steps.every((s, i) => s.eventName === stepNames[i]),
  );

  // Falls back to counting sessions from the healthcare graph only if the
  // healthcare profile is active — pharma has no session data so this
  // branch is unreachable there (list_funnels only advertises the pharma
  // funnels, and analytics_funnel_query is only invoked against advertised
  // funnels).
  const counts = match
    ? match.stepCounts
    : getPreviewProfile() === "pharma"
      ? stepNames.map(() => 0)
      : stepNames.map((_, i) =>
          countStep(getPreviewGraph().sessions, stepNames, i),
        );

  const firstCount = counts[0] ?? 0;
  return counts.map((count, i) => {
    const prev = i > 0 ? counts[i - 1] : firstCount;
    return {
      stepIndex: i,
      stepName: params.steps[i].eventName,
      count,
      conversionRate: firstCount === 0 ? 0 : (count / firstCount) * 100,
      dropoffPct: i === 0 || prev === 0 ? 0 : ((prev - count) / prev) * 100,
    };
  });
}

export function handleFunnelQuery(
  params: PageViewFunnelQueryRequest,
): FunnelStepResult[] {
  const funnels = activeFunnels();
  const patternSteps = params.steps
    .filter(
      (s): s is Extract<FunnelStepConfig, { type: "pageView" }> =>
        s.type === "pageView",
    )
    .map((s) => ({
      urlPatterns: s.urlPatterns,
    }));

  const match = funnels.find(
    (f) =>
      f.steps.length === patternSteps.length &&
      f.steps.every(
        (s, i) =>
          s.urlPatterns.length === patternSteps[i].urlPatterns.length &&
          s.urlPatterns.every((p, j) => p === patternSteps[i].urlPatterns[j]),
      ),
  );

  const counts = match
    ? match.stepCounts
    : getPreviewProfile() === "pharma"
      ? patternSteps.map(() => 0)
      : computePatternStepCounts(getPreviewGraph().sessions, patternSteps);

  const firstCount = counts[0] ?? 0;
  return counts.map((count, i) => {
    const prev = i > 0 ? counts[i - 1] : firstCount;
    return {
      stepIndex: i,
      stepName: params.steps[i].label || `Step ${i + 1}`,
      count,
      conversionRate: firstCount === 0 ? 0 : (count / firstCount) * 100,
      dropoffPct: i === 0 || prev === 0 ? 0 : ((prev - count) / prev) * 100,
    };
  });
}
